import {
    StyleProp,
    ImageStyle,
    Image,
    Pressable,
    View,
    GestureResponderEvent,
} from "react-native";
import * as React from "react";
import CachedImage from "../../components/CachedImage";
import { useMemo, useState, useRef, useCallback } from "react";
import { enlargeAvatarData } from "../../atoms/avatar";
import { useAtomValue, useSetAtom } from "jotai";

type UserAvatarProps = {
    avatarPath?: string | number | null;
    size?: number;
    style?: StyleProp<ImageStyle>;
    testID?: string;
    canEnlarge?: boolean;
};

const UserAvatar: React.FC<UserAvatarProps> = ({
    avatarPath,
    size = 55,
    style,
    testID,
    canEnlarge = true,
}: UserAvatarProps) => {
    const enlargeAvatarAtom = useAtomValue(enlargeAvatarData);
    const setEnlargeAvatarAtom = useSetAtom(enlargeAvatarData);

    const [isVisible, setIsVisible] = useState(true);
    const baseStyle = useMemo<StyleProp<ImageStyle>>(
        () => ({
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: "#eee",
            overflow: "hidden" as const,
            opacity: +isVisible,
        }),
        [isVisible, size],
    );

    const imageProps = useMemo(
        () => ({
            testID,
            style: [baseStyle, style],
            resizeMode: "cover" as const,
        }),
        [baseStyle, style, testID],
    );

    const ref = useRef<View>(null);

    // Re-show the avatar when the enlarged modal closes (atom cleared). The
    // local `isVisible` state is toggled off on press so the original avatar
    // disappears under the modal animation; this restores it on dismiss.
    // Adjust-on-prop-change pattern keyed on the atom transitioning to null.
    const [prevEnlargeAvatarAtom, setPrevEnlargeAvatarAtom] = useState(enlargeAvatarAtom);
    if (enlargeAvatarAtom !== prevEnlargeAvatarAtom) {
        setPrevEnlargeAvatarAtom(enlargeAvatarAtom);
        if (!enlargeAvatarAtom && !isVisible) {
            setIsVisible(true);
        }
    }

    const onPressAvatar = useCallback(
        (e: GestureResponderEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (avatarPath) {
                ref.current?.measureInWindow((x, y) => {
                    setEnlargeAvatarAtom({
                        image: {
                            avatarPath,
                            props: imageProps,
                        },
                        initialPosition: {
                            pageX: x,
                            pageY: y,
                            size,
                        },
                    });
                    setIsVisible(false);
                });
            }
        },
        [avatarPath, imageProps, setEnlargeAvatarAtom, size],
    );

    if (!avatarPath) {
        return null;
    }

    let content;

    if (typeof avatarPath === "number") {
        // number is used for require()d images
        content = <Image source={avatarPath} {...imageProps} />;
    } else {
        content = <CachedImage source={avatarPath} {...imageProps} />;
    }

    if (!canEnlarge) {
        return content;
    }

    return (
        <Pressable ref={ref} onPress={onPressAvatar}>
            {content}
        </Pressable>
    );
};

export default UserAvatar;
